/**
 * Product shots of the landing page, drawn with the real UI kit (ScorePill, ToPar, FlagChip,
 * StatusChip, KpiCard, Avatar) and sample data instead of screenshots: they stay sharp, follow
 * the UI language and always match the current design tokens. Each preview is one labelled image
 * for assistive technology (role="img"), the inner markup is decorative.
 */
import clsx from 'clsx';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  CloudOff,
  Hourglass,
  Minus,
  PenLine,
  Plus,
  Radio,
  Trophy,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoMark, Wordmark } from '../../components/brand/Logo';
import { Avatar, FlagChip, KpiCard, ScorePill, StatusChip, ToPar } from '../../components/ui';

const TOURNAMENT = 'KATPGA Tân Sơn Nhất Open 2026';

interface Row {
  pos: string;
  move: number;
  nat: string;
  name: string;
  bib: number;
  toPar: number;
  thru: string;
  today: number;
}

/* sample field (names are data, not UI strings) */
const ROWS: Row[] = [
  { pos: '1', move: 1, nat: 'KR', name: '정서연', bib: 57, toPar: -7, thru: '14', today: -3 },
  { pos: '2', move: 2, nat: 'VN', name: 'Võ Công Nam', bib: 40, toPar: -6, thru: '15*', today: -3 },
  { pos: '3', move: 0, nat: 'KR', name: '한영호', bib: 9, toPar: -5, thru: '16', today: -3 },
  {
    pos: 'T4',
    move: 3,
    nat: 'VN',
    name: 'Hoàng Minh Kiên',
    bib: 25,
    toPar: -4,
    thru: '12*',
    today: -3,
  },
  {
    pos: 'T4',
    move: -1,
    nat: 'VN',
    name: 'Nguyễn Thị Thu Hà',
    bib: 112,
    toPar: -4,
    thru: 'F',
    today: -2,
  },
  { pos: 'T6', move: 4, nat: 'JP', name: 'Takumi Sato', bib: 94, toPar: -3, thru: '11', today: -3 },
  {
    pos: 'T6',
    move: -2,
    nat: 'TH',
    name: 'Somchai Wongsakul',
    bib: 77,
    toPar: -3,
    thru: '13*',
    today: -1,
  },
];

function Move({ value }: { value: number }) {
  if (value === 0) return <span className="lp-move">–</span>;
  return (
    <span className={clsx('lp-move', value > 0 ? 'lp-move--up' : 'lp-move--down')}>
      {value > 0 ? <ArrowUp size={11} strokeWidth={3} /> : <ArrowDown size={11} strokeWidth={3} />}
      {Math.abs(value)}
    </span>
  );
}

function Shot({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={clsx('lp-shot', className)} role="img" aria-label={label}>
      <div aria-hidden="true" className="lp-shot__inner">
        {children}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- leaderboard */

export function LeaderboardPreview({ rows = 6, compact }: { rows?: number; compact?: boolean }) {
  const { t } = useTranslation('auth');
  return (
    <Shot
      label={t('landing.shots.leaderboard')}
      className={clsx('lp-board', compact && 'lp-board--compact')}
    >
      <div className="lp-board__bar">
        <span className="lp-dots">
          <i />
          <i />
          <i />
        </span>
        <span className="lp-board__url">pingo · /t/…</span>
      </div>
      <div className="lp-board__hero">
        <div>
          <StatusChip tone="live">LIVE</StatusChip>
          <span className="lp-board__round">{t('landing.preview.roundLive')}</span>
        </div>
        <strong className="lp-board__title">{TOURNAMENT}</strong>
        <span className="lp-board__meta">{t('landing.preview.venue')}</span>
      </div>
      <div className="lp-board__table">
        <div className="lp-board__head">
          <span>{t('landing.preview.pos')}</span>
          <span>{t('landing.preview.player')}</span>
          <span>{t('landing.preview.toPar')}</span>
          <span>{t('landing.preview.thru')}</span>
          <span className="lp-hide-sm">{t('landing.preview.today')}</span>
        </div>
        {ROWS.slice(0, rows).map((r, i) => (
          <div key={r.bib} className={clsx('lp-board__row', i === 0 && 'lp-board__row--lead')}>
            <span className="lp-board__pos num">{r.pos}</span>
            <span className="lp-board__name">
              <Move value={r.move} />
              <FlagChip code={r.nat} showCode={false} />
              <b>{r.name}</b>
              <small>#{r.bib}</small>
            </span>
            <ToPar value={r.toPar} size="lg" />
            <span className="num lp-board__thru">{r.thru}</span>
            <span className="lp-hide-sm">
              <ToPar value={r.today} size="sm" />
            </span>
          </div>
        ))}
      </div>
    </Shot>
  );
}

/* ---------------------------------------------------------------- caddy phone */

const CADDY_ROWS = [
  { bib: 12, nat: 'KR', name: '윤하늘', strokes: 4, state: 'saved' as const },
  { bib: 66, nat: 'VN', name: 'Đỗ Minh Tuấn', strokes: 5, state: 'pending' as const },
  { bib: 83, nat: 'KR', name: '강지우', strokes: 3, state: 'pending' as const },
];

export function CaddyPhonePreview({
  offline,
  players = 3,
}: {
  offline?: boolean;
  players?: number;
}) {
  const { t } = useTranslation('auth');
  return (
    <Shot label={t('landing.shots.caddy')} className="lp-phone">
      <div className="lp-phone__screen" data-contrast="outdoor">
        <div className="lp-phone__status">
          <span>10:42</span>
          <span className="lp-phone__notch" />
          <span>5G</span>
        </div>
        <div className="lp-caddy__top">
          <div>
            <strong>{t('landing.preview.caddyGroup')}</strong>
            <small>C012 · R2</small>
          </div>
          <span className={clsx('lp-caddy__sync', offline && 'lp-caddy__sync--off')}>
            {offline ? <CloudOff size={14} /> : <Radio size={14} />}
            {offline ? t('landing.preview.offlineQueue') : t('landing.preview.onlineQueue')}
          </span>
        </div>
        <div className="lp-caddy__holes">
          {[5, 6, 7, 8, 9].map((h) => (
            <span
              key={h}
              className={clsx(
                'lp-caddy__hole',
                h < 7 && 'is-done',
                h === 7 && 'is-pending',
                h === 8 && 'is-current',
              )}
            >
              {h}
              {h < 7 ? <Check size={12} /> : h === 7 ? <Hourglass size={12} /> : <small>P4</small>}
            </span>
          ))}
        </div>
        <div className="lp-caddy__holebar">
          <div>
            <strong>{t('landing.preview.holeTitle')}</strong>
            <small>{t('landing.preview.holeMeta')}</small>
          </div>
          <span className="lp-caddy__nav">
            <ChevronLeft size={18} />
          </span>
          <span className="lp-caddy__nav">
            <ChevronRight size={18} />
          </span>
        </div>
        {CADDY_ROWS.slice(0, players).map((r) => (
          <div key={r.bib} className="lp-caddy__row">
            <div className="lp-caddy__who">
              <span className="lp-caddy__bib">{r.bib}</span>
              <FlagChip code={r.nat} showCode={false} />
              <b>{r.name}</b>
              <span className={clsx('lp-caddy__state', `lp-caddy__state--${r.state}`)}>
                {r.state === 'saved' ? <Check size={12} /> : <Clock size={12} />}
                {r.state === 'saved' ? t('landing.preview.saved') : t('landing.preview.pending')}
              </span>
            </div>
            <div className="lp-caddy__entry">
              <span className="lp-caddy__key">
                <Minus size={20} />
              </span>
              <span
                className={clsx(
                  'lp-caddy__value',
                  r.strokes < 4 && 'is-under',
                  r.strokes > 4 && 'is-over',
                )}
              >
                {r.strokes}
              </span>
              <span className="lp-caddy__key">
                <Plus size={20} />
              </span>
              <span className="lp-caddy__putts">
                {[0, 1, 2, 3].map((p) => (
                  <i key={p} className={p === (r.strokes === 3 ? 1 : 2) ? 'is-on' : undefined}>
                    {p}
                  </i>
                ))}
              </span>
            </div>
          </div>
        ))}
        <div className="lp-caddy__save">{t('landing.preview.reviewSave')}</div>
      </div>
    </Shot>
  );
}

/* ---------------------------------------------------------------- TV */

export function TvPreview() {
  const { t } = useTranslation('auth');
  return (
    <Shot label={t('landing.shots.tv')} className="lp-tv">
      <div className="lp-tv__screen" data-theme="dark">
        <div className="lp-tv__head">
          <LogoMark size={26} ring />
          <div>
            <strong>{TOURNAMENT}</strong>
            <small>{t('landing.preview.roundLive')}</small>
          </div>
          <span className="lp-tv__live">LIVE</span>
        </div>
        {ROWS.slice(0, 5).map((r, i) => (
          <div key={r.bib} className={clsx('lp-tv__row', i === 0 && 'is-lead')}>
            <span className="lp-tv__pos num">{r.pos}</span>
            <FlagChip code={r.nat} showCode={false} />
            <b>{r.name}</b>
            <ToPar value={r.toPar} size="lg" />
            <span className="num lp-tv__thru">{r.thru}</span>
          </div>
        ))}
        <div className="lp-tv__ticker">
          <span className="lp-tv__tag">{t('landing.preview.birdie')}</span>
          <FlagChip code="KR" showCode={false} />
          <span>정서연 · {t('landing.preview.tickerHole')}</span>
          <span className="lp-tv__brand">
            <Wordmark onDark />
          </span>
        </div>
      </div>
      <div className="lp-tv__stand" />
    </Shot>
  );
}

/* ---------------------------------------------------------------- admin console */

export function ConsolePreview() {
  const { t } = useTranslation('auth');
  const groups = Array.from({ length: 18 }, (_, i) => i + 1);
  const late = new Set([6, 14]);
  const queued = new Set([8]);
  return (
    <Shot label={t('landing.shots.console')} className="lp-console">
      <div className="lp-console__side">
        <LogoMark size={22} />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <i key={i} className={i === 0 ? 'is-on' : undefined} />
        ))}
      </div>
      <div className="lp-console__main">
        <div className="lp-console__title">
          <strong>{TOURNAMENT}</strong>
          <StatusChip tone="success">{t('landing.preview.inProgress')}</StatusChip>
        </div>
        <div className="lp-console__kpis">
          <KpiCard label={t('landing.preview.kpiProgress')} value="64" unit="%" progress={0.64} />
          <KpiCard label={t('landing.preview.kpiUnsigned')} value="4" tone="warning" />
          <KpiCard label={t('landing.preview.kpiAnomalies')} value="3" tone="danger" />
        </div>
        <div className="lp-console__grid">
          <div className="lp-console__holes">
            <small>{t('landing.preview.groupsOnCourse')}</small>
            <div>
              {groups.map((h) => (
                <span key={h} className="lp-console__hole">
                  <em>{h}</em>
                  {h % 3 !== 0 ? (
                    <b className={clsx(late.has(h) && 'is-late', queued.has(h) && 'is-queued')}>
                      {(h * 7) % 30 || 30}
                    </b>
                  ) : null}
                </span>
              ))}
            </div>
          </div>
          <div className="lp-console__alerts">
            <small>{t('landing.preview.anomalies')}</small>
            <p className="is-high">
              <AlertTriangle size={14} />
              {t('landing.preview.alertBurst')}
            </p>
            <p>
              <PenLine size={14} />
              {t('landing.preview.alertEdits')}
            </p>
            <p>
              <Clock size={14} />
              {t('landing.preview.alertOffline')}
            </p>
          </div>
        </div>
      </div>
    </Shot>
  );
}

/* ---------------------------------------------------------------- club (member variant) */

const STANDINGS = [
  { name: '박성진', pts: 412, hi: 8.4 },
  { name: 'Lâm Gia Bảo', pts: 388, hi: 12.1 },
  { name: '최유나', pts: 351, hi: 15.6 },
];

export function ClubPreview() {
  const { t } = useTranslation('auth');
  return (
    <Shot label={t('landing.shots.club')} className="lp-club">
      <div className="lp-club__screen" data-variant="member">
        <div className="lp-club__next">
          <small>{t('landing.preview.nextTee')}</small>
          <strong>{t('landing.preview.clubEvent')}</strong>
          <div className="lp-club__facts">
            <span>
              <b className="num">06:42</b>
              <small>{t('landing.preview.teeTime')}</small>
            </span>
            <span>
              <b className="num">5</b>
              <small>{t('landing.preview.group')}</small>
            </span>
            <span>
              <b className="num">A·B</b>
              <small>{t('landing.preview.course')}</small>
            </span>
          </div>
        </div>
        <div className="lp-club__season">
          <div className="lp-club__season-head">
            <Trophy size={16} />
            <strong>{t('landing.preview.season')}</strong>
          </div>
          {STANDINGS.map((s, i) => (
            <div key={s.name} className="lp-club__row">
              <span className="num lp-club__rank">{i + 1}</span>
              <Avatar name={s.name} size={28} tone={i === 0 ? 'sun' : 'neutral'} />
              <b>{s.name}</b>
              <span className="lp-club__hi">
                HI <b className="num">{s.hi.toFixed(1)}</b>
              </span>
              <span className="num lp-club__pts">{s.pts}</span>
            </div>
          ))}
          <p className="lp-club__note">{t('landing.preview.hiUnofficial')}</p>
        </div>
      </div>
    </Shot>
  );
}

/* ---------------------------------------------------------------- scorecard strip */

const CARD = [
  { h: 1, par: 4, s: 4 },
  { h: 2, par: 4, s: 3 },
  { h: 3, par: 3, s: 3 },
  { h: 4, par: 5, s: 4 },
  { h: 5, par: 4, s: 3 },
  { h: 6, par: 4, s: 5 },
  { h: 7, par: 3, s: 3 },
  { h: 8, par: 4, s: 4 },
  { h: 9, par: 5, s: 3 },
];

export function ScorecardStrip() {
  const { t } = useTranslation('auth');
  return (
    <Shot label={t('landing.shots.scorecard')} className="lp-card">
      <div className="lp-card__row lp-card__row--head">
        <span>{t('landing.preview.hole')}</span>
        {CARD.map((c) => (
          <span key={c.h}>{c.h}</span>
        ))}
        <span>OUT</span>
      </div>
      <div className="lp-card__row lp-card__row--par">
        <span>Par</span>
        {CARD.map((c) => (
          <span key={c.h}>{c.par}</span>
        ))}
        <span>36</span>
      </div>
      <div className="lp-card__row">
        <span>R2</span>
        {CARD.map((c) => (
          <span key={c.h}>
            <ScorePill strokes={c.s} par={c.par} size="sm" />
          </span>
        ))}
        <b className="num">32</b>
      </div>
    </Shot>
  );
}
